// Inspired by https://github.com/FormidableLabs/urql
// from file urql/exchanges/persisted-fetch/src/sha256.ts
// with slight modifications for better typing and improved code quality
import type { Nullable } from 'corgi-types';

import { getMessageFromError } from '../../utils/error-handlers';
import { captureMessage } from '../../utils/error-reporter';
import { isClient } from '../../utils/isClient';

declare global {
  interface Crypto {
    webkitSubtle?: SubtleCrypto;
  }
}

let sha256BrowserFunction: Nullable<(bytes: Uint8Array) => Promise<Uint8Array>> = null;
let sha256Initialized = false;

const initSha256BrowserCrypto = () => {
  const jsCrypto = window.crypto;
  const cryptoSubtle = jsCrypto?.subtle || jsCrypto?.webkitSubtle;
  const isCryptoAvailable = !!cryptoSubtle?.digest;
  if (isCryptoAvailable) {
    sha256BrowserFunction = async (bytes: Uint8Array): Promise<Uint8Array> => {
      const result = await cryptoSubtle.digest(
        { name: 'SHA-256' },
        bytes as Uint8Array<ArrayBuffer>
      );

      return new Uint8Array(result);
    };
  }

  return sha256BrowserFunction;
};

const getSha256Browser = () => {
  if (sha256Initialized) {
    return sha256BrowserFunction;
  } else {
    sha256Initialized = true;

    return (sha256BrowserFunction = initSha256BrowserCrypto());
  }
};

const onCryptoNotAvailable = () => {
  const errorMessage = '[SHA256]: Crypto.subtle.digest is not available';
  captureMessage(errorMessage);
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error(errorMessage);
  }
};

const queryToUint8Array = (query: string) => {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(query);
  } else {
    const bytes = new Uint8Array(query.length);
    for (let i = 0, l = query.length; i < l; i++) {
      // NOTE: We assume that the input GraphQL Query only uses UTF-8 at most
      // since GraphQL mostly consists of ASCII, this is completely fine
      bytes[i] = query.charCodeAt(i);
    }

    return bytes;
  }
};

const formatHash = (bytes: Uint8Array) => {
  let hash = '';
  for (let i = 0, length = bytes.length; i < length; i++) {
    const hex = bytes[i].toString(16);
    hash += '00'.slice(0, Math.max(0, 2 - hex.length)) + hex;
  }

  return hash;
};

const onSha256Error = (err: unknown) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error('[SHA256]: There was problem with hashing query', err);
  }
  const message = getMessageFromError(err);
  if (message) {
    captureMessage(message);
  }
};

export const sha256 = async (query: string): Promise<string> => {
  if (!isClient()) {
    return '';
  }

  const sha256Browser = getSha256Browser();
  if (!sha256Browser) {
    onCryptoNotAvailable();

    return '';
  }

  try {
    const inputBytes = queryToUint8Array(query);
    const hashedBytes = await sha256Browser(inputBytes);

    return formatHash(hashedBytes);
  } catch (err) {
    onSha256Error(err);

    return '';
  }
};
